import { useId, useMemo, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { Popover, PopoverBody } from 'reactstrap';
import { BiCalendar, BiX } from 'react-icons/bi';
import { ymdToLocalDate, localDateToYmd, formatDdMmYyyy } from './dateHelpers';
import CalendarDropdown from './calendarDropdown';

/* ---------- Local date helpers for preset computation (no date-fns) ----- */
const startOfDay = () => new Date(new Date().setHours(0, 0, 0, 0));
const addDays = (d, n) => {
    const c = new Date(d);
    c.setDate(c.getDate() + n);
    return c;
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);
const endOfYear = (d) => new Date(d.getFullYear(), 11, 31);

/* ---------- Built-in presets -------------------------------------------- */
// Each returns a fresh { from, to } pair computed at click-time. Kept inline
// so the component ships with a sensible default set but consumers can pass
// their own `presets` without us shipping a shared constant.
function defaultPresets() {
    return [
        {
            label: 'Today',
            range: () => {
                const d = startOfDay();
                return { from: d, to: d };
            },
        },
        {
            label: 'Yesterday',
            range: () => {
                const d = addDays(startOfDay(), -1);
                return { from: d, to: d };
            },
        },
        {
            label: 'Last 7 days',
            range: () => ({ from: addDays(startOfDay(), -6), to: startOfDay() }),
        },
        {
            label: 'Last 30 days',
            range: () => ({ from: addDays(startOfDay(), -29), to: startOfDay() }),
        },
        {
            label: 'This month',
            range: () => {
                const now = startOfDay();
                return { from: startOfMonth(now), to: endOfMonth(now) };
            },
        },
        {
            label: 'Last month',
            range: () => {
                const prev = new Date(startOfDay().getFullYear(), startOfDay().getMonth() - 1, 1);
                return { from: startOfMonth(prev), to: endOfMonth(prev) };
            },
        },
        {
            label: 'This year',
            range: () => {
                const now = startOfDay();
                return { from: startOfYear(now), to: endOfYear(now) };
            },
        },
    ];
}

/* ---------- Component --------------------------------------------------- */
export default function DateRangePicker({
    value,
    handleChange,
    disableWeekends,
    isClearable = true,
    disabled,
    fromDate,
    toDate,
    presets,
    placeholder = 'dd/mm/yyyy – dd/mm/yyyy',
}) {
    const [open, setOpen] = useState(false);
    const triggerId = `drp-${useId().replace(/:/g, '_')}`;

    const from = value?.from ?? '';
    const to = value?.to ?? '';

    const selected = useMemo(
        () => ({ from: ymdToLocalDate(from), to: ymdToLocalDate(to) }),
        [from, to]
    );

    const display = useMemo(() => {
        if (!from && !to) return '';
        const f = from ? formatDdMmYyyy(from) : 'dd/mm/yyyy';
        const t = to ? formatDdMmYyyy(to) : '…';
        return `${f} – ${t}`;
    }, [from, to]);

    const disabledMatcher = useMemo(
        () =>
            [
                disableWeekends && { dayOfWeek: [0, 6] },
                fromDate && { before: ymdToLocalDate(fromDate) },
                toDate && { after: ymdToLocalDate(toDate) },
            ].filter(Boolean),
        [disableWeekends, fromDate, toDate]
    );

    const onRangeSelect = (range) => {
        if (!range) {
            handleChange({ from: '', to: '' });
            return;
        }
        const next = {
            from: range.from ? localDateToYmd(range.from) : '',
            to: range.to ? localDateToYmd(range.to) : '',
        };
        handleChange(next);
        // Close only once both ends are picked, so the user gets a chance
        // to select the second date without fighting a re-opening popover.
        if (range.from && range.to) setOpen(false);
    };

    const applyPreset = (preset) => {
        const r = preset.range();
        handleChange({ from: localDateToYmd(r.from), to: localDateToYmd(r.to) });
        setOpen(false);
    };

    const presetList = presets ?? defaultPresets();

    return (
        <>
            <div className="dp-trigger-wrap">
                <button
                    id={triggerId}
                    type="button"
                    className={`form-control dp-trigger${display ? '' : ' is-placeholder'}`}
                    disabled={disabled}
                    onClick={() => setOpen((o) => !o)}
                >
                    {display || placeholder}
                </button>
                <div className="dp-trigger__icons">
                    {(!!from || !!to) && isClearable && !disabled && (
                        <button
                            type="button"
                            className="dp-trigger__icon-btn"
                            aria-label="Clear range"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleChange({ from: '', to: '' });
                            }}
                        >
                            <BiX size={16} />
                        </button>
                    )}
                    <BiCalendar size={16} />
                </div>
            </div>

            <Popover
                isOpen={open}
                target={triggerId}
                placement="bottom-start"
                toggle={() => setOpen((o) => !o)}
                trigger="legacy"
                popperClassName="dp-popover"
                hideArrow
                fade={false}
            >
                <PopoverBody>
                    <div className="dp-range-body">
                        <div className="dp-presets">
                            {presetList.map((p) => (
                                <button
                                    key={p.label}
                                    type="button"
                                    className="dp-preset"
                                    onClick={() => applyPreset(p)}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <DayPicker
                            mode="range"
                            numberOfMonths={2}
                            selected={selected}
                            onSelect={onRangeSelect}
                            disabled={disabledMatcher}
                            weekStartsOn={1}
                            captionLayout="dropdown"
                            components={{ Dropdown: CalendarDropdown }}
                        />
                    </div>
                </PopoverBody>
            </Popover>
        </>
    );
}
